import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { FaChartPie, FaUserGraduate, FaMoneyBillWave, FaTimes, FaSearch, FaCheckCircle, FaCalendarCheck } from "react-icons/fa";
import { HiDocumentReport } from "react-icons/hi";
import { MdPayment } from "react-icons/md";
import { BsClockHistory } from "react-icons/bs";
import { BiLoaderCircle } from "react-icons/bi";
import { RiAlarmWarningFill } from "react-icons/ri";
import LineChart from "../../context/LineChart";
import DoughnutChart from "../../context/DoughnutChart";
import GenderDoughnutChart from "../../context/GenderDoughnutChart";

const Dashboard = () => {
  // Telefon görüntü formatı: (0XXX) XXX XX XX
  
  const navigate = useNavigate();
  
  // API verisi için state'ler
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state'leri
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [showLatestStudentsModal, setShowLatestStudentsModal] = useState(false);
  
  // Tahsilat modal state'leri
  const [showMonthlyIncomeModal, setShowMonthlyIncomeModal] = useState(false);
  const [showTodayCollectionModal, setShowTodayCollectionModal] = useState(false);
  const [showTodayExpectedModal, setShowTodayExpectedModal] = useState(false);
  
  // Arama ve tarih filtre state'leri
  const [upcomingSearch, setUpcomingSearch] = useState("");
  const [overdueSearch, setOverdueSearch] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  
  const [upcomingStartDate, setUpcomingStartDate] = useState("");
  const [upcomingEndDate, setUpcomingEndDate] = useState("");
  
  const [overdueEndDate, setOverdueEndDate] = useState("");
  const [overdueStartDate, setOverdueStartDate] = useState("");

  // Beklenen gelir tarih filtresi (default: son ay)
  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDayOfMonth.toISOString().split('T')[0],
      end: lastDayOfMonth.toISOString().split('T')[0]
    };
  };

  const defaultDateRange = getDefaultDateRange();
  const [expectedIncomeStartDate, setExpectedIncomeStartDate] = useState(defaultDateRange.start);
  const [expectedIncomeEndDate, setExpectedIncomeEndDate] = useState(defaultDateRange.end);

  
  // Öğrenci detay sayfasına yönlendirme fonksiyonu
  const navigateToStudentDetails = (studentId, event) => {
    // Event propagation'ı durdur
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log("Öğrenci ID:", studentId);
    
    // Öğrenci bilgilerini bul
    const student = students.find(s => s.id === studentId);
    console.log("Bulunan öğrenci:", student);
    
    if (student) {
      navigate(`/student-list/student-details/${studentId}`, { 
        state: { student } 
      });
    } else {
      console.error("Öğrenci bulunamadı:", studentId);
    }
  };
  
  // Veri çekme fonksiyonu
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
      // API istekleri
        const token = localStorage.getItem("authToken");
        const timestamp = new Date().getTime();
      const studentsResponse = await axios.get(
        `http://esnakademi.com/api/students?_t=${timestamp}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setStudents(studentsResponse.data);
    } catch (err) {
      console.error("API hatası:", err);
      setError(
        "Veri yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin."
      );
      } finally {
        setLoading(false);
      }
    };
    
  // Sayfa yüklendiğinde veri çek
  useEffect(() => {
    fetchDashboardData();
    
    // Ödeme verisi değiştiğinde dashboard'ı güncelle
    const handlePaymentDataChange = () => {
      fetchDashboardData();
    };
    
    window.addEventListener('paymentDataChanged', handlePaymentDataChange);
    
    // Cleanup function
    return () => {
      window.removeEventListener('paymentDataChanged', handlePaymentDataChange);
    };
  }, []);
  
  // Güvenli tarih dönüştürme yardımcı fonksiyonu
  const parseDate = (dateString) => {
    try {
      // Eğer bir tarih nesnesi ise doğrudan döndür
      if (dateString instanceof Date) return dateString;

      // String değil ise null döndür
      if (typeof dateString !== "string") return null;

      // Uzun tarih formatını kontrol et (Mon Apr 28 2025 03:00:00 GMT+0300)
      if (dateString.includes("GMT")) {
        return new Date(dateString);
      }

      // ISO formatını kontrol et
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }

      // Farklı tarih formatlarını dene
      // DD/MM/YYYY
      const parts = dateString.split(/[/.-]/);
      if (parts.length === 3) {
        // Gün/ay/yıl formatı olabilir
        if (parts[0].length <= 2 && parts[1].length <= 2) {
          return new Date(parts[2], parts[1] - 1, parts[0]);
        }
        // Yıl-ay-gün formatı olabilir
        else if (parts[0].length === 4) {
          return new Date(parts[0], parts[1] - 1, parts[2]);
        }
      }

      // Başarısız olursa null döndür
      return null;
    } catch (error) {
      console.error("Tarih ayrıştırma hatası:", error, dateString);
      return null;
    }
  };

  // Ödeme planı JSON parse fonksiyonu
  const parsePaymentPlan = (paymentPlanJson) => {
    try {
      if (!paymentPlanJson) return null;

      let str = paymentPlanJson.trim();
      // Eğer ilk karakter { değilse ilk { ve son } arasını al
      const first = str.indexOf('{');
      const last = str.lastIndexOf('}');
      if (first !== -1 && last !== -1) {
        str = str.slice(first, last + 1);
      }
      str = str.replace(/\\"/g, '"');
      return JSON.parse(str);
    } catch (error) {
      console.error("Ödeme planı ayrıştırma hatası:", error);
      return null;
    }
  };

  // Öğrencilerden yaklaşan ödemeleri al
  const getUpcomingPayments = useMemo(() => {
    if (loading || !students.length) return [];

    const now = new Date();
    const allPayments = [];

    students.forEach((student) => {
      const paymentPlan = parsePaymentPlan(student.payment_plan);
      if (!paymentPlan || !paymentPlan.unpaidBills) return;

      // Ödenmemiş faturaları kontrol et
      paymentPlan.unpaidBills.forEach((bill) => {
        const dueDate = parseDate(bill.dueDate);
        if (!dueDate || dueDate <= now) return; // Sadece gelecek ödemeler

        allPayments.push({
          id: bill.id,
          student_id: student.id,
          studentName: `${student.name} ${student.surname}`,
          amount: bill.amount,
          dueDate: dueDate,
          daysDue: Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24)),
          motherPhone: student.mother_phone || "-",
          fatherPhone: student.father_phone || "-"
        });
      });
    });

    // Tarihe göre sırala (önce en yakın ödeme)
    return allPayments.sort((a, b) => a.dueDate - b.dueDate);
  }, [students, loading]);

  // Öğrencilerden gecikmiş ödemeleri al
  const getOverduePayments = useMemo(() => {
    if (loading || !students.length) return [];

    const now = new Date();
    const allOverduePayments = [];

    students.forEach((student) => {
      const paymentPlan = parsePaymentPlan(student.payment_plan);
      if (!paymentPlan || !paymentPlan.unpaidBills) return;

      // Ödenmemiş faturaları kontrol et
      paymentPlan.unpaidBills.forEach((bill) => {
        const dueDate = parseDate(bill.dueDate);
        if (!dueDate || dueDate > now) return; // Sadece geçmiş ödemeler

        allOverduePayments.push({
          id: bill.id,
          student_id: student.id,
          studentName: `${student.name} ${student.surname}`,
          amount: bill.amount,
          dueDate: dueDate,
          daysOverdue: Math.ceil((now - dueDate) / (1000 * 60 * 60 * 24)), // Gecikme gün sayısı
          motherPhone: student.mother_phone || "-",
          fatherPhone: student.father_phone || "-"
        });
      });
    });

    // Tarihe göre sırala (en çok gecikmiş önce)
    return allOverduePayments.sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [students, loading]);

  // İstatistik değerlerini hesapla
  const stats = useMemo(() => {
    if (loading)
      return {
        totalStudents: 0,
        activeCourses: 0,
        upcomingPayments: 0,
        overduePayments: 0,
        overduePaymentsAmount: 0,
        monthlyIncome: 0,
        todayCollections: 0,
        todayExpected: 0,
      };

    // Öğrenci sayısı
    const totalStudents = students.length;
    // Yaklaşan ödemeler (30 gün içinde)
    const now = new Date();
    const in30Days = new Date(now);
    in30Days.setDate(now.getDate() + 30);

    let upcomingPayments = 0;
    let overduePayments = 0;
    let overduePaymentsAmount = 0;
    let expectedMonthlyIncome = 0;

    // Bu ayki gelir, yaklaşan ve gecikmiş ödemeleri hesapla
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Bugün saat 00:00:00
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1); // Yarın saat 00:00:00

    let todayCollections = 0; // Bugün yapılan tahsilat
    let todayExpected = 0; // Bugün beklenen tahsilat

    students.forEach((student) => {
      // DETAY TABLOSU İLE TAMAMEN AYNI PARSE MANTIGI
      let paymentPlan;
      try {
        paymentPlan = typeof student.payment_plan === 'string' 
          ? JSON.parse(student.payment_plan) 
          : student.payment_plan;
      } catch (e) {
        return; // Parse error
      }
      
      if (!paymentPlan) return;

      // Ödenmemiş faturaları kontrol et
      if (paymentPlan?.unpaidBills) {
        // Yaklaşan ödemeleri hesapla (30 gün içinde)
        upcomingPayments += paymentPlan.unpaidBills.filter((bill) => {
          const dueDate = parseDate(bill.dueDate);
          return dueDate && dueDate > now && dueDate <= in30Days;
        }).length;

        // Gecikmiş ödemeleri hesapla
        const overdueBills = paymentPlan.unpaidBills.filter((bill) => {
          const dueDate = parseDate(bill.dueDate);
          return dueDate && dueDate < now;
        });
        
        overduePayments += overdueBills.length;
        
        // Gecikmiş ödemelerin toplam tutarını hesapla
        overduePaymentsAmount += overdueBills.reduce(
          (sum, bill) => sum + (parseFloat(bill.amount) || 0),
          0
        );
        
        // Tarih filtresine göre beklenen gelir hesapla (unpaid bills)
        paymentPlan.unpaidBills.forEach(bill => {
          const dueDate = parseDate(bill.dueDate);
          if (dueDate) {
            const dueDateStr = dueDate.toISOString().split('T')[0];
            if (dueDateStr >= expectedIncomeStartDate && dueDateStr <= expectedIncomeEndDate) {
              expectedMonthlyIncome += parseFloat(bill.amount) || 0;
            }
          }
        });

        // Bugün vadesi gelen henüz ödenmemiş faturaları hesapla
        const todayDueBills = paymentPlan.unpaidBills.filter((bill) => {
          const dueDate = parseDate(bill.dueDate);
          return dueDate && dueDate >= today && dueDate < tomorrow;
        });
        
        // Bugün beklenen tahsilatı hesapla
        todayExpected += todayDueBills.reduce(
          (sum, bill) => sum + (parseFloat(bill.amount) || 0),
          0
        );
      }

      // Tarih filtresine göre beklenen gelir hesapla (paid installments)
      if (paymentPlan?.paidInstallments) {
        paymentPlan.paidInstallments.forEach(paid => {
          const paymentDate = parseDate(paid.paymentDate);
          if (paymentDate) {
            const paymentDateStr = paymentDate.toISOString().split('T')[0];
            if (paymentDateStr >= expectedIncomeStartDate && paymentDateStr <= expectedIncomeEndDate) {
              expectedMonthlyIncome += parseFloat(paid.amount) || 0;
            }
          }
        });

        // Bugün yapılan ödemeleri hesapla
        const todayPayments = paymentPlan.paidInstallments.filter(
          (payment) => {
            const paymentDate = parseDate(payment.paymentDate);
            return paymentDate && paymentDate >= today && paymentDate < tomorrow;
          }
        );

        // Bugün yapılan tahsilatı hesapla
        todayCollections += todayPayments.reduce(
          (sum, payment) => sum + (parseFloat(payment.amount) || 0),
          0
        );
      }
    });

    return {
      totalStudents,
      upcomingPayments,
      overduePayments,
      overduePaymentsAmount,
      monthlyIncome: expectedMonthlyIncome,
      todayCollections,
      todayExpected,
    };
  }, [students, loading, expectedIncomeStartDate, expectedIncomeEndDate]);

  // Grafik verileri için hesaplamalar
  const chartData = useMemo(() => {
    if (loading || !students.length) {
      return {
        gender: null,
        classes: null,
        line: null,
      };
    }

    // Cinsiyet dağılımı
    const female = students.filter(
      (student) => {
        const g = (student.gender || "").toLowerCase();
        return g === "female" || g === "kız" || g === "kadın" || g === "f" || g === "k";
      }
    ).length;
    
    const male = students.filter(
      (student) => {
        const g = (student.gender || "").toLowerCase();
        return g === "male" || g === "erkek" || g === "bay" || g === "m" || g === "e";
      }
    ).length;

    console.log("Cinsiyet dağılımı:", { male, female, total: students.length });

    const genderData = {
      male,
      female,
    };

    // Sınıf dağılımı
    const classGroups = {};
    students.forEach((student) => {
      const grade = student.student_class; // Sınıf seviyesi
      if (grade) {
        classGroups[grade] = (classGroups[grade] || 0) + 1;
      }
    });

    // Sınıf dağılım verisi - diziye dönüştür
    const classData = Object.keys(classGroups)
      .sort((a, b) => a - b) // Sınıfları sırala
      .map((grade) => ({
        name: `${grade}. Sınıf`,
        count: classGroups[grade],
      }));

    // Aylık kayıt sayıları
    const monthlyRegistrations = {};

    students.forEach((student) => {
      const registerDate = parseDate(student.registration_date);
      if (!registerDate) return;

      const month = registerDate.getMonth();
      const year = registerDate.getFullYear();
      const key = `${year}-${month + 1}`;

      monthlyRegistrations[key] = (monthlyRegistrations[key] || 0) + 1;
    });

    // Son 6 ayın verisini al
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);

      const month = date.getMonth();
      const year = date.getFullYear();
      const key = `${year}-${month + 1}`;

      const monthName = new Intl.DateTimeFormat("tr-TR", {
        month: "short",
      }).format(date);

      last6Months.push({
        key,
        label: `${monthName}`,
        count: monthlyRegistrations[key] || 0,
        date: new Date(year, month, 1),
      });
    }

    // Aylık ödeme verileri
    const monthlyPayments = {};

    students.forEach((student) => {
      const paymentPlan = parsePaymentPlan(student.payment_plan);
      if (!paymentPlan || !paymentPlan.paidInstallments) return;

      paymentPlan.paidInstallments.forEach((payment) => {
        const paymentDate = parseDate(payment.paymentDate);
        if (!paymentDate) return;

        const month = paymentDate.getMonth();
        const year = paymentDate.getFullYear();
        const key = `${year}-${month + 1}`;

        const amount = parseFloat(payment.amount) || 0;
        monthlyPayments[key] = (monthlyPayments[key] || 0) + amount;
      });
    });

    // Son 6 ayın ödeme verilerini al
    const last6MonthsPayments = last6Months.map((month) => ({
      ...month,
      amount: monthlyPayments[month.key] || 0,
    }));

    // Line chart için veri
    const lineData = {
      labels: last6Months.map((month) => month.label),
      datasets: [
        {
          label: "Öğrenci Kayıtları",
          data: last6Months.map((month) => month.count),
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.3,
          fill: true,
        },
        {
          label: "Ödemeler (₺ x 1000)",
          data: last6MonthsPayments.map((month) => month.amount / 1000), // Binlik gösterim için
          borderColor: "rgb(16, 185, 129)",
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          tension: 0.3,
          fill: true,
          yAxisID: "y1",
        },
      ],
    };

    // Line chart için seçenekler
    const lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        tooltip: {
          mode: "index",
          intersect: false,
        },
      },
      scales: {
        y: {
          type: "linear",
          display: true,
          position: "left",
          title: {
            display: true,
            text: "Öğrenci Sayısı",
          },
          beginAtZero: true,
        },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: "Ödeme (₺ x 1000)",
          },
          beginAtZero: true,
        },
      },
    };

    return {
      gender: genderData,
      classes: classData,
      line: { data: lineData, options: lineOptions },
    };
  }, [students, loading]);

  // En son kayıt olan 5 öğrenci
  const latestStudents = useMemo(() => {
    if (loading || !students.length) return [];

    return [...students]
      .filter((student) => parseDate(student.registration_date)) // Sadece geçerli tarihleri al
      .sort((a, b) => {
        const dateA = parseDate(a.registration_date);
        const dateB = parseDate(b.registration_date);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map((student) => ({
        id: student.id,
        name: `${student.name} ${student.surname}`,
        class: `${student.student_class || "-"}. Sınıf`,
        registerDate:
          parseDate(student.registration_date)?.toLocaleDateString("tr-TR") ||
          "-",
        motherPhone: student.mother_phone,
        fatherPhone: student.father_phone,
        parentPhone: student.mother_phone || student.father_phone || "-",
        createdBy: student.advisor_teacher || student.created_by_username || "-",
      }));
  }, [students, loading]);

  // Yaklaşan ödemeler
  const upcomingPaymentsList = useMemo(() => {
    return getUpcomingPayments.slice(0, 5).map((payment) => ({
      ...payment,
      dueDate: payment.dueDate.toLocaleDateString("tr-TR"),
    }));
  }, [getUpcomingPayments]);

  // Gecikmiş ödemeler
  const overduePaymentsList = useMemo(() => {
    return getOverduePayments.slice(0, 5).map((payment) => ({
      ...payment,
      dueDate: payment.dueDate.toLocaleDateString("tr-TR"),
    }));
  }, [getOverduePayments]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <BiLoaderCircle className="text-blue-500 animate-spin mb-4" size={48} />
        <h2 className="text-lg text-gray-700 font-medium">
          Dashboard yükleniyor...
        </h2>
        <p className="text-sm text-gray-500 mt-2">
          Veriler hazırlanıyor, lütfen bekleyin.
        </p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <h2 className="text-lg text-red-700 font-medium mb-2">Hata Oluştu</h2>
        <p className="text-red-600">{error}</p>
          <button 
          onClick={fetchDashboardData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
          Yeniden Dene
          </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <FaUserGraduate size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Toplam Öğrenci</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalStudents}
              </h3>
            </div>
          </div>
          <div className="mt-4">
            <Link
              to="/student-list"
              className="text-blue-600 text-sm hover:underline"
            >
              Tüm öğrencileri görüntüle →
            </Link>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4">
              <MdPayment size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Yaklaşan Ödemeler</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.upcomingPayments}
              </h3>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowUpcomingModal(true)}
              className="text-purple-600 text-sm hover:underline"
            >
              Ödemeleri görüntüle →
            </button>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <RiAlarmWarningFill size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Gecikmiş Ödemeler</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.overduePayments}
              </h3>
              <span className="text-sm font-semibold text-red-600">
                {new Intl.NumberFormat("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                }).format(stats.overduePaymentsAmount)}
              </span>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowOverdueModal(true)}
              className="text-red-600 text-sm hover:underline"
            >
              Tahsilatı görüntüle →
            </button>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-amber-100 text-amber-600 mr-4">
              <FaMoneyBillWave size={24} />
            </div>
              <div>
              <p className="text-sm text-gray-500 mb-1">Beklenen Gelir</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {new Intl.NumberFormat("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                }).format(stats.monthlyIncome)}
              </h3>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => setShowMonthlyIncomeModal(true)}
              className="text-amber-600 text-sm hover:underline flex items-center"
            >
              Detayları görüntüle →
            </button>
          </div>

            </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <FaCheckCircle size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Bugün Yapılan Tahsilat</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {new Intl.NumberFormat("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                }).format(stats.todayCollections)}
              </h3>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => setShowTodayCollectionModal(true)}
              className="text-green-600 text-sm hover:underline flex items-center"
            >
              Detayları görüntüle →
            </button>
          </div>

        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-teal-100">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-teal-100 text-teal-600 mr-4">
              <FaCalendarCheck size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Bugün Beklenen Tahsilat</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {new Intl.NumberFormat("tr-TR", {
                  style: "currency",
                  currency: "TRY",
                }).format(stats.todayExpected)}
              </h3>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={() => setShowTodayExpectedModal(true)}
              className="text-teal-600 text-sm hover:underline flex items-center"
            >
              Detayları görüntüle →
            </button>
          </div>
        </div>
      </div>
          
      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaChartPie className="text-blue-500 mr-2" />
              Öğrenci ve Ödeme Trendi
            </h3>
            <div style={{ height: "350px" }}>
              {chartData.line ? (
                <LineChart
                  customData={chartData.line.data}
                  customOptions={chartData.line.options}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">Veri yükleniyor...</p>
              </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <GenderDoughnutChart data={chartData.gender} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <DoughnutChart data={chartData.classes} />

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <HiDocumentReport className="text-green-500 mr-2" />
            Son Kayıt Olan Öğrenciler
          </h3>

          {latestStudents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Öğrenci
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sınıf
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kayıt T.
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Anne Tel.
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Baba Tel.
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kaydı Yapan
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {latestStudents.map((student, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <button 
                          onClick={(e) => navigateToStudentDetails(student.id, e)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                        >
                          {student.name}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {student.class}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {student.registerDate}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {student.motherPhone || "-"}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {student.fatherPhone || "-"}
                      </td>
                      <td className="py-2 px-3 text-sm text-blue-600 font-medium">
                        {student.createdBy}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Henüz öğrenci kaydı bulunmuyor.</p>
            </div>
          )}

          <div className="mt-4 text-right">
            <button
              onClick={() => setShowLatestStudentsModal(true)}
              className="text-blue-600 text-sm hover:underline"
            >
              Tüm öğrencileri görüntüle →
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Yaklaşan Ödemeler */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BsClockHistory className="text-purple-500 mr-2" />
            Yaklaşan Ödemeler
          </h3>

          {upcomingPaymentsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Öğrenci
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tutar
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Son Ödeme
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kalan Gün
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Anne Tel.
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Baba Tel.
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {upcomingPaymentsList.map((payment, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${
                        payment.daysDue <= 3 ? "bg-amber-50" : ""
                      }`}
                    >
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <button 
                          onClick={(e) => navigateToStudentDetails(payment.student_id, e)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                        >
                          {payment.studentName}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-sm font-medium text-gray-900">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(payment.amount)}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.dueDate instanceof Date ? payment.dueDate.toLocaleDateString("tr-TR") : payment.dueDate}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium 
                          ${
                            payment.daysDue <= 3
                              ? "bg-amber-100 text-amber-800"
                              : payment.daysDue <= 7
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {payment.daysDue} gün
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.motherPhone}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.fatherPhone}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Yaklaşan ödeme bulunmuyor.</p>
            </div>
          )}

          <div className="mt-4 text-right">
            <button
              onClick={() => setShowUpcomingModal(true)}
              className="text-purple-600 text-sm hover:underline"
            >
              Tüm ödemeleri görüntüle →
            </button>
          </div>
        </div>
        
        {/* Gecikmiş Ödemeler */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-red-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <RiAlarmWarningFill className="text-red-500 mr-2" />
            Gecikmiş Ödemeler
          </h3>

          {overduePaymentsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Öğrenci
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tutar
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Son Ödeme
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gecikme
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Anne Tel.
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Baba Tel.
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {overduePaymentsList.map((payment, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${
                        payment.daysOverdue > 30 ? "bg-red-50" : "bg-red-50/50"
                      }`}
                    >
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <button 
                          onClick={(e) => navigateToStudentDetails(payment.student_id, e)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                        >
                          {payment.studentName}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-sm font-medium text-gray-900">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(payment.amount)}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.dueDate instanceof Date ? payment.dueDate.toLocaleDateString("tr-TR") : payment.dueDate}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium 
                          ${
                            payment.daysOverdue > 30
                              ? "bg-red-100 text-red-800"
                              : payment.daysOverdue > 15
                              ? "bg-red-100/80 text-red-800"
                              : "bg-red-100/60 text-red-800"
                          }`}
                        >
                          {payment.daysOverdue} gün
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.motherPhone}
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-900">
                        {payment.fatherPhone}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Gecikmiş ödeme bulunmuyor.</p>
            </div>
          )}

          <div className="mt-4 text-right">
            <button
              onClick={() => setShowOverdueModal(true)}
              className="text-red-600 text-sm hover:underline"
            >
              Tüm tahsilatları görüntüle →
            </button>
          </div>
        </div>
      </div>

      {/* Yaklaşan Ödemeler Modal */}
      {showUpcomingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-purple-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <BsClockHistory className="text-purple-500 mr-2" />
                Yaklaşan Tüm Ödemeler
              </h2>
              <button 
                onClick={() => setShowUpcomingModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={upcomingSearch}
                  onChange={(e) => setUpcomingSearch(e.target.value)}
                  className="pl-10 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Öğrenci adına göre ara..."
                />
              </div>
              {/* Tarih filtreleri */}
              <p className="mt-4 text-xs text-gray-500">Belirli bir tarih aralığında vadesi yaklaşan ödemeleri görmek için aralık seçin:</p>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor="upcoming-start">Başlangıç</label>
                  <input
                    id="upcoming-start"
                    type="date"
                    value={upcomingStartDate}
                    onChange={(e) => setUpcomingStartDate(e.target.value)}
                    className="p-2 border rounded-md w-full"
                    placeholder="Başlangıç"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor="upcoming-end">Bitiş</label>
                  <input
                    id="upcoming-end"
                    type="date"
                    value={upcomingEndDate}
                    onChange={(e) => setUpcomingEndDate(e.target.value)}
                    className="p-2 border rounded-md w-full"
                    placeholder="Bitiş"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>
            </div>
            
            <div className="overflow-y-auto p-4 flex-grow">
              {getUpcomingPayments.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Son Ödeme</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kalan Gün</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anne Tel.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Baba Tel.</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getUpcomingPayments
                      .filter(payment => payment.studentName.toLowerCase().includes(upcomingSearch.toLowerCase()))
                      .filter(payment => {
                         if(upcomingStartDate){ if(payment.dueDate < new Date(upcomingStartDate)) return false; }
                         if(upcomingEndDate){ if(payment.dueDate > new Date(upcomingEndDate)) return false; }
                         return true;
                      })
                      .map((payment, index) => (
                      <tr key={index} className={`hover:bg-gray-50 ${payment.daysDue <= 3 ? "bg-amber-50" : ""}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          <button 
                            onClick={(e) => navigateToStudentDetails(payment.student_id, e)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                          >
                            {payment.studentName}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(payment.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.dueDate instanceof Date ? payment.dueDate.toLocaleDateString("tr-TR") : payment.dueDate}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium 
                            ${
                              payment.daysDue <= 3
                                ? "bg-amber-100 text-amber-800"
                                : payment.daysDue <= 7
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"}`}>
                              {payment.daysDue} gün
                            </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.motherPhone}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.fatherPhone}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Yaklaşan ödeme bulunmuyor.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowUpcomingModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors ml-auto block"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gecikmiş Ödemeler Modal */}
      {showOverdueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-red-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <RiAlarmWarningFill className="text-red-500 mr-2" />
                Gecikmiş Tüm Ödemeler
              </h2>
              <button 
                onClick={() => setShowOverdueModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={overdueSearch}
                  onChange={(e) => setOverdueSearch(e.target.value)}
                  className="pl-10 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Öğrenci adına göre ara..."
                />
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor="overdue-start">Başlangıç</label>
                  <input
                    id="overdue-start"
                    type="date"
                    value={overdueStartDate}
                    onChange={(e)=>setOverdueStartDate(e.target.value)}
                    className="p-2 border rounded-md w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1" htmlFor="overdue-end">Bitiş</label>
                  <input
                    id="overdue-end"
                    type="date"
                    value={overdueEndDate}
                    onChange={(e)=>setOverdueEndDate(e.target.value)}
                    className="p-2 border rounded-md w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>
            </div>
            
            <div className="overflow-y-auto p-4 flex-grow">
              {getOverduePayments.length > 0 ? (
                <table id="overdue-table" className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Son Ödeme</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gecikme</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anne Tel.</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Baba Tel.</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getOverduePayments
                      .filter(payment => payment.studentName.toLowerCase().includes(overdueSearch.toLowerCase()))
                      .filter(payment => {
                        if(overdueStartDate){ if(payment.dueDate < new Date(overdueStartDate)) return false; }
                        if(overdueEndDate){ if(payment.dueDate > new Date(overdueEndDate)) return false; }
                        return true;
                      })
                      .map((payment, index) => (
                      <tr key={index} className={`hover:bg-gray-50 ${payment.daysOverdue > 30 ? "bg-red-50" : "bg-red-50/50"}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          <button 
                            onClick={(e) => navigateToStudentDetails(payment.student_id, e)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                          >
                            {payment.studentName}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(payment.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.dueDate instanceof Date ? payment.dueDate.toLocaleDateString("tr-TR") : payment.dueDate}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium 
                            ${
                              payment.daysOverdue > 30
                                ? "bg-red-100 text-red-800"
                                : payment.daysOverdue > 15
                                ? "bg-red-100/80 text-red-800"
                                : "bg-red-100/60 text-red-800"}`}>
                              {payment.daysOverdue} gün
                            </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.motherPhone}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {payment.fatherPhone}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Gecikmiş ödeme bulunmuyor.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => {
                  // Filtrelenmiş listeyi al
                  const list = getOverduePayments
                    .filter(payment => payment.studentName.toLowerCase().includes(overdueSearch.toLowerCase()))
                    .filter(payment => {
                      if (overdueStartDate && payment.dueDate < new Date(overdueStartDate)) return false;
                      if (overdueEndDate && payment.dueDate > new Date(overdueEndDate)) return false;
                      return true;
                    });

                  const wsData = [
                    ["Öğrenci","Tutar","Son Ödeme","Gecikme","Anne Tel.","Baba Tel."]
                  ];
                  list.forEach(p => {
                    wsData.push([
                      p.studentName,
                      p.amount,
                      p.dueDate instanceof Date ? p.dueDate.toLocaleDateString('tr-TR') : p.dueDate,
                      p.daysOverdue,
                      p.motherPhone,
                      p.fatherPhone
                    ]);
                  });

                  const wb = XLSX.utils.book_new();
                  const ws = XLSX.utils.aoa_to_sheet(wsData);
                  XLSX.utils.book_append_sheet(wb, ws, 'Gecikmis');
                  XLSX.writeFile(wb, 'gecikmis_odemeler.xlsx');
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Excel İndir
              </button>

              <button
                onClick={() => {
                  const printContents = document.getElementById('overdue-table').outerHTML;
                  const newWin = window.open('', '', 'width=900,height=650');
                  newWin.document.write('<html><head><title>Gecikmiş Ödemeler</title>');
                  newWin.document.write('<style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;text-align:left;font-size:12px}</style>');
                  newWin.document.write('</head><body>');
                  newWin.document.write(printContents);
                  newWin.document.write('</body></html>');
                  newWin.document.close();
                  newWin.print();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Yazdır
              </button>

              <button
                onClick={() => setShowOverdueModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Son Kayıt Olan Öğrenciler Modal */}
      {showLatestStudentsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-blue-50">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FaUserGraduate className="text-blue-500 mr-2" />
                Son Kayıt Olan Tüm Öğrenciler
              </h2>
              <button 
                onClick={() => setShowLatestStudentsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="pl-10 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Öğrenci adına göre ara..."
                />
              </div>
            </div>
            
            <div className="overflow-y-auto p-4 flex-grow">
              {students.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öğrenci</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınıf</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Anne Telefonu</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Baba Telefonu</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kaydı Yapan</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students
                      .filter(student => parseDate(student.registration_date))
                      .filter(student => 
                        (student.name + " " + student.surname).toLowerCase().includes(studentSearch.toLowerCase())
                      )
                      .sort((a, b) => {
                        const dateA = parseDate(a.registration_date);
                        const dateB = parseDate(b.registration_date);
                        return dateB - dateA;
                      })
                      .map((student, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            <button 
                              onClick={(e) => navigateToStudentDetails(student.id, e)}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer bg-transparent border-none p-0 text-left"
                            >
                              {student.name} {student.surname}
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {student.student_class || "-"}. Sınıf
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {parseDate(student.registration_date)?.toLocaleDateString("tr-TR") || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {student.mother_phone || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {student.father_phone || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 font-medium">
                            {student.advisor_teacher || "-"}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Öğrenci kaydı bulunmuyor.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowLatestStudentsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors ml-auto block"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beklenen Gelir Modal */}
      {showMonthlyIncomeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
              <h3 className="text-lg font-semibold text-amber-800 mb-4">Beklenen Gelir Detayları</h3>
              
              {/* Tarih Filtresi */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={expectedIncomeStartDate}
                    onChange={(e) => setExpectedIncomeStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    value={expectedIncomeEndDate}
                    onChange={(e) => setExpectedIncomeEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <div>
                  <button
                    onClick={() => {
                      const defaultRange = getDefaultDateRange();
                      setExpectedIncomeStartDate(defaultRange.start);
                      setExpectedIncomeEndDate(defaultRange.end);
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                  >
                    Son Aya Sıfırla
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Toplam Özet */}
              {(() => {
                let totalUnpaidThisMonth = 0;
                let totalPaidThisMonth = 0;
                
                students.forEach(student => {
                  try {
                    const paymentPlan = typeof student.payment_plan === 'string' 
                      ? JSON.parse(student.payment_plan) 
                      : student.payment_plan;
                    
                    if (paymentPlan?.unpaidBills) {
                      paymentPlan.unpaidBills.forEach(bill => {
                        const dueDate = parseDate(bill.dueDate);
                        if (dueDate) {
                          const dueDateStr = dueDate.toISOString().split('T')[0];
                          if (dueDateStr >= expectedIncomeStartDate && dueDateStr <= expectedIncomeEndDate) {
                            totalUnpaidThisMonth += parseFloat(bill.amount) || 0;
                          }
                        }
                      });
                    }
                    
                    if (paymentPlan?.paidInstallments) {
                      paymentPlan.paidInstallments.forEach(paid => {
                        const paymentDate = parseDate(paid.paymentDate);
                        if (paymentDate) {
                          const paymentDateStr = paymentDate.toISOString().split('T')[0];
                          if (paymentDateStr >= expectedIncomeStartDate && paymentDateStr <= expectedIncomeEndDate) {
                            totalPaidThisMonth += parseFloat(paid.amount) || 0;
                          }
                        }
                      });
                    }
                  } catch (e) {
                    // Parse error
                  }
                });
                
                const grandTotal = totalUnpaidThisMonth + totalPaidThisMonth;
                
                return (
                  <div className="mb-6 p-4 bg-amber-100 rounded-lg border border-amber-300">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <span className="text-gray-600 text-sm">Bekleyen:</span>
                        <div className="font-semibold text-amber-700 text-lg">
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(totalUnpaidThisMonth)}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Ödenen:</span>
                        <div className="font-semibold text-green-700 text-lg">
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(totalPaidThisMonth)}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm">Toplam:</span>
                        <div className="font-bold text-amber-800 text-lg">
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: "TRY",
                          }).format(grandTotal)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Detay Listesi */}
              <div className="space-y-2">
                {/* Ödenmemiş Taksitler */}
                {students.map(student => {
                  const monthlyUnpaid = [];
                  try {
                    const paymentPlan = typeof student.payment_plan === 'string' 
                      ? JSON.parse(student.payment_plan) 
                      : student.payment_plan;
                    
                    if (paymentPlan?.unpaidBills) {
                      paymentPlan.unpaidBills.forEach(bill => {
                        const dueDate = parseDate(bill.dueDate);
                        if (dueDate) {
                          const dueDateStr = dueDate.toISOString().split('T')[0];
                          if (dueDateStr >= expectedIncomeStartDate && dueDateStr <= expectedIncomeEndDate) {
                            monthlyUnpaid.push({
                              ...bill,
                              student_name: `${student.name} ${student.surname}`
                            });
                          }
                        }
                      });
                    }
                  } catch (e) {
                    // Parse error
                  }
                  
                  return monthlyUnpaid.map((installment, index) => (
                    <div key={`unpaid-${student.id}-${index}`} className="flex justify-between items-center py-3 px-4 bg-white rounded border border-amber-100 hover:bg-amber-50">
                      <span className="text-gray-700">
                        {installment.student_name} - {installment.id}. Taksit
                      </span>
                      <span className="font-semibold text-amber-700">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(installment.amount)}
                      </span>
                    </div>
                  ));
                }).flat()}
                
                {/* Bu ay ödenmiş taksitler */}
                {students.map(student => {
                  const paidThisMonth = [];
                  try {
                    const paymentPlan = typeof student.payment_plan === 'string' 
                      ? JSON.parse(student.payment_plan) 
                      : student.payment_plan;
                    
                    if (paymentPlan?.paidInstallments) {
                      paymentPlan.paidInstallments.forEach(paid => {
                        const paymentDate = parseDate(paid.paymentDate);
                        if (paymentDate) {
                          const paymentDateStr = paymentDate.toISOString().split('T')[0];
                          if (paymentDateStr >= expectedIncomeStartDate && paymentDateStr <= expectedIncomeEndDate) {
                            paidThisMonth.push({
                              ...paid,
                              student_name: `${student.name} ${student.surname}`
                            });
                          }
                        }
                      });
                    }
                  } catch (e) {
                    // Parse error
                  }
                  
                  return paidThisMonth.map((payment, index) => (
                    <div key={`paid-${student.id}-${index}`} className="flex justify-between items-center py-3 px-4 bg-green-50 rounded border border-green-100 hover:bg-green-100">
                      <span className="text-gray-700">
                        {payment.student_name} - {payment.id}. Taksit (Ödendi)
                      </span>
                      <span className="font-semibold text-green-700">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(payment.amount)}
                      </span>
                    </div>
                  ));
                }).flat()}

                {/* Boş durum */}
                {(students.map(student => {
                  const paymentPlan = typeof student.payment_plan === 'string' 
                    ? JSON.parse(student.payment_plan) 
                    : student.payment_plan;
                  const filteredItems = [
                    ...(paymentPlan?.unpaidBills?.filter(bill => {
                      const dueDate = parseDate(bill.dueDate);
                      if (dueDate) {
                        const dueDateStr = dueDate.toISOString().split('T')[0];
                        return dueDateStr >= expectedIncomeStartDate && dueDateStr <= expectedIncomeEndDate;
                      }
                      return false;
                    }) || []),
                    ...(paymentPlan?.paidInstallments?.filter(paid => {
                      const paymentDate = parseDate(paid.paymentDate);
                      if (paymentDate) {
                        const paymentDateStr = paymentDate.toISOString().split('T')[0];
                        return paymentDateStr >= expectedIncomeStartDate && paymentDateStr <= expectedIncomeEndDate;
                      }
                      return false;
                    }) || [])
                  ];
                  return filteredItems;
                }).flat().length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    Seçilen tarih aralığı için beklenen gelir kaydı bulunamadı
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowMonthlyIncomeModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors ml-auto block"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bugün Yapılan Tahsilat Modal */}
      {showTodayCollectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <h3 className="text-lg font-semibold text-green-800">Bugün Yapılan Tahsilat Detayları</h3>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                {students.map(student => {
                  const todayPayments = [];
                  try {
                    const paymentPlan = typeof student.payment_plan === 'string' 
                      ? JSON.parse(student.payment_plan) 
                      : student.payment_plan;
                    
                    if (paymentPlan?.paidInstallments) {
                      const today = new Date();
                      paymentPlan.paidInstallments.forEach(paid => {
                        const paymentDate = parseDate(paid.paymentDate);
                        if (paymentDate && 
                            paymentDate.getDate() === today.getDate() && 
                            paymentDate.getMonth() === today.getMonth() && 
                            paymentDate.getFullYear() === today.getFullYear()) {
                          todayPayments.push({
                            ...paid,
                            student_name: `${student.name} ${student.surname}`
                          });
                        }
                      });
                    }
                  } catch (e) {
                    // Parse error
                  }
                  
                  return todayPayments.map((payment, index) => (
                    <div key={`today-${student.id}-${index}`} className="flex justify-between items-center py-3 px-4 bg-white rounded border border-green-100 hover:bg-green-50">
                      <span className="text-gray-700">
                        {payment.student_name} - {payment.id}. Taksit
                        <span className="text-gray-500 text-xs ml-2">
                          ({payment.paymentMethod || 'Nakit'})
                        </span>
                      </span>
                      <span className="font-semibold text-green-700">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(payment.amount)}
                      </span>
                    </div>
                  ));
                }).flat()}
                
                {students.map(student => {
                  const paymentPlan = typeof student.payment_plan === 'string' 
                    ? JSON.parse(student.payment_plan) 
                    : student.payment_plan;
                  const today = new Date();
                  return paymentPlan?.paidInstallments?.filter(paid => {
                    const paymentDate = parseDate(paid.paymentDate);
                    return paymentDate && 
                           paymentDate.getDate() === today.getDate() && 
                           paymentDate.getMonth() === today.getMonth() && 
                           paymentDate.getFullYear() === today.getFullYear();
                  }) || [];
                }).flat().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Bugün henüz tahsilat yapılmadı
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowTodayCollectionModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors ml-auto block"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bugün Beklenen Tahsilat Modal */}
      {showTodayExpectedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-teal-50">
              <h3 className="text-lg font-semibold text-teal-800">Bugün Beklenen Tahsilat Detayları</h3>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                {students.map(student => {
                  const todayDue = [];
                  try {
                    const paymentPlan = typeof student.payment_plan === 'string' 
                      ? JSON.parse(student.payment_plan) 
                      : student.payment_plan;
                    
                    if (paymentPlan?.unpaidBills) {
                      const today = new Date();
                      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const tomorrow = new Date(todayStart);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      
                      paymentPlan.unpaidBills.forEach(bill => {
                        const dueDate = parseDate(bill.dueDate);
                        if (dueDate && dueDate >= todayStart && dueDate < tomorrow) {
                          todayDue.push({
                            ...bill,
                            student_name: `${student.name} ${student.surname}`
                          });
                        }
                      });
                    }
                  } catch (e) {
                    // Parse error
                  }
                  
                  return todayDue.map((bill, index) => (
                    <div key={`due-${student.id}-${index}`} className="flex justify-between items-center py-3 px-4 bg-white rounded border border-teal-100 hover:bg-teal-50">
                      <span className="text-gray-700">
                        {bill.student_name} - {bill.id}. Taksit
                        <span className="text-gray-500 text-xs ml-2">
                          (Vade: {new Date(bill.dueDate).toLocaleDateString('tr-TR')})
                        </span>
                      </span>
                      <span className="font-semibold text-teal-700">
                        {new Intl.NumberFormat("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                        }).format(bill.amount)}
                      </span>
                    </div>
                  ));
                }).flat()}
                
                {students.map(student => {
                  const paymentPlan = typeof student.payment_plan === 'string' 
                    ? JSON.parse(student.payment_plan) 
                    : student.payment_plan;
                  const today = new Date();
                  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const tomorrow = new Date(todayStart);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  
                  return paymentPlan?.unpaidBills?.filter(bill => {
                    const dueDate = parseDate(bill.dueDate);
                    return dueDate && dueDate >= todayStart && dueDate < tomorrow;
                  }) || [];
                }).flat().length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Bugün beklenen tahsilat bulunmuyor
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowTodayExpectedModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors ml-auto block"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
